import { Button, Input, currencyToNumber } from '@iwoca/orion';
import { SubmitHandler, useForm } from 'react-hook-form';
import messages from './CreateNewApplicationForm.messages';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchPutState,
    fetchGetStateByStateKey,
    PostApplicationRequestBody,
    fetchPutApplication,
    PutApplicationRequestBody,
    PutStateRequestBody,
    GetStateResponse
} from '@iwoca/lapi-client/edge';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { useGetCustomerState } from '@api/lendingAPI/lapiHooks';
import { produce } from 'immer';
import { formaliseOffers } from '@api/formaliseOffers';
import { LoadingAnimation } from '../../../ui/loadingAnimation/LoadingAnimation';
import { getCountry } from '@iwIntlProvider/CountryProvider';

const MAX_REQUEST_AMOUNT = 500000;

export type TApplicationStatus =
    | 'auto_approved'
    | 'auto_declined'
    | 'requires_documents'
    | 'docs_complete'
    | 'unknown'
    | null;

type TFormValues = {
    turnover: string;
    credit: number;
};

export const CreateNewApplicationForm = ({
    accountId,
    setApplicationStatus,
    amountFieldTitle,
    turnoverFieldTitle
}: {
    accountId: string;
    setApplicationStatus: React.Dispatch<
        React.SetStateAction<TApplicationStatus>
    >;
    amountFieldTitle?: React.ReactElement;
    turnoverFieldTitle?: React.ReactElement;
}) => {
    const queryClient = useQueryClient();
    const intl = useIntl();
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));

    const {
        data: customerStateResponse,
        isLoading: customerStateLoading,
        isError: customerStateError
    } = useGetCustomerState(stateKey);

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        },
        onError: () => {
            throw Error('Failed to update customer state');
        }
    });

    const createApplication = useMutation({
        mutationFn: (application: PutApplicationRequestBody) =>
            fetchPutApplication({
                stateKey: stateKey,
                body: application
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['application', stateKey]
            });
        }
    });

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid, isSubmitting }
    } = useForm<TFormValues>({
        mode: 'onChange'
    });

    // TODO
    if (accountsLoading || customerStateLoading) return null;
    if (accountsError || customerStateError) return null;

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const currentPrincipal = account.balance!.principal!;

    const turnover =
        customerStateResponse?.application?.company?.last_12_months_turnover
            ?.amount;

    const currency = {
        locale: intl.locale,
        currencySymbol: true,
        minorUnits: false
    };

    const maxRequestAmountMinusCurrentPrincipal =
        MAX_REQUEST_AMOUNT - currentPrincipal;

    const onSubmit: SubmitHandler<TFormValues> = async (formData) => {
        const customerState = await queryClient.fetchQuery({
            queryKey: ['customer_state', stateKey],
            queryFn: () => fetchGetStateByStateKey({ stateKey })
        });

        const newCustomerState = JSON.parse(JSON.stringify(customerState));

        newCustomerState.data.application.company.last_12_months_turnover = {
            amount: formData.turnover,
            datetime: new Date().toISOString()
        };

        // Combining the outstanding balance and requested amount to better reflect what
        // we think customers think they ask for
        const combinedPrincipalAndRequestedAmount =
            formData.credit + currentPrincipal;

        const application = {
            data: {
                requested_products: [
                    {
                        amount: combinedPrincipalAndRequestedAmount,
                        product_type: 'credit_facility',
                        requested_cash_amount: formData.credit
                    }
                ]
            }
        } as PostApplicationRequestBody;

        try {
            await saveCustomerState.mutateAsync(newCustomerState);

            await createApplication.mutateAsync(application);

            const useAutoFormalisation = getCountry() === 'UK';
            const { result } = await formaliseOffers({
                stateKey,
                useAutoFormalisation
            });

            switch (result) {
                case 'FORMALISED':
                    await queryClient.invalidateQueries({
                        queryKey: ['accounts']
                    });
                    setApplicationStatus('auto_approved');
                    break;
                case 'DECLINED':
                    await queryClient.invalidateQueries({
                        queryKey: ['application', stateKey]
                    });
                    setApplicationStatus('auto_declined');
                    break;
                case 'DOCS_COMPLETE':
                    setApplicationStatus('docs_complete');
                    break;
                case 'DOCS_REQUIRED':
                    // Have to access query client directly to ensure the latest response is used
                    const latestCustomerStateResponse: GetStateResponse =
                        queryClient.getQueryData(['customer_state', stateKey])!;

                    const newCustomerState = produce(
                        latestCustomerStateResponse!,
                        (draft) => {
                            // @ts-expect-error Have to ignore this because UI is not included in the type for the /state PUT request body from LAPI client
                            draft.data.ui = {
                                hasCreatedFlexiApplication: true
                            };
                        }
                    );

                    await saveCustomerState.mutateAsync(newCustomerState);

                    setApplicationStatus('requires_documents');
                    break;
            }
        } catch (err) {
            console.log(err);
        }
    };

    if (isSubmitting)
        return (
            <div className="flex flex-col items-center justify-center">
                <LoadingAnimation data-testid="loading-spinner" />
                <p>
                    Please wait, it can take up to 30 seconds to submit your
                    application
                </p>
            </div>
        );

    const watchCredit = watch('credit');
    const availableCash = account.latest_formal_offer?.available_cash!;
    const isPreApprovedOffer = account.latest_formal_offer?.is_pre_approval;

    const isPreApprovedWithAvailableCash =
        isPreApprovedOffer && availableCash > 0;
    const requestedAmountLessThanAvailableCash =
        isPreApprovedWithAvailableCash &&
        watchCredit >= 1000 &&
        watchCredit <= availableCash;

    return (
        <form
            className="flex flex-col gap-2xl"
            onSubmit={handleSubmit(onSubmit)}
        >
            <Input
                {...register('credit', {
                    setValueAs: (val) => currencyToNumber(val, currency),
                    required: intl.formatMessage(messages.creditError),
                    min: {
                        value: 1000,
                        message: intl.formatMessage(messages.minCreditError, {
                            minAmount: intlFormatCurrency({
                                intl: intl,
                                value: 1000
                            })
                        })
                    },
                    max: {
                        value: maxRequestAmountMinusCurrentPrincipal,
                        message: intl.formatMessage(messages.maxCreditError, {
                            maxAmount: intlFormatCurrency({
                                intl: intl,
                                value: maxRequestAmountMinusCurrentPrincipal,
                                decimalPlaces: 2
                            })
                        })
                    }
                })}
                id="credit"
                label={
                    amountFieldTitle ||
                    intl.formatMessage(messages.creditQuestion)
                }
                type="currency"
                placeholder={`e.g. ${intlFormatCurrency({
                    intl: intl,
                    value: 10000
                })}`}
                currency={currency}
                error={errors.credit?.message}
                // @ts-expect-error
                success={
                    (requestedAmountLessThanAvailableCash &&
                        intl.formatMessage(messages.alreadyApprovedMessage, {
                            approvalAmount: intlFormatCurrency({
                                intl,
                                value: availableCash,
                                decimalPlaces: 2
                            }),
                            hereCta: (
                                <span
                                    onClick={() =>
                                        setApplicationStatus('auto_approved')
                                    }
                                    className="ml-xs text-primary-40 underline hover:cursor-pointer"
                                >
                                    here
                                </span>
                            )
                        })) ||
                    ''
                }
            />
            <Input
                {...register('turnover', {
                    setValueAs: (val) => currencyToNumber(val, currency),
                    required: intl.formatMessage(messages.turnoverError),
                    min: {
                        value: 1,
                        message: intl.formatMessage(messages.minTurnoverError, {
                            minAmount: intlFormatCurrency({
                                intl: intl,
                                value: 0
                            })
                        })
                    }
                })}
                id="turnover"
                label={
                    turnoverFieldTitle ||
                    intl.formatMessage(messages.turnoverQuestion)
                }
                type="currency"
                currency={currency}
                placeholder={`e.g. ${intlFormatCurrency({
                    intl: intl,
                    value: 100000
                })}`}
                defaultValue={turnover}
                error={errors.turnover?.message}
            />
            <Button
                type="submit"
                variant="primary"
                disabled={!isValid || requestedAmountLessThanAvailableCash}
                loading={isSubmitting}
                className="mx-auto my-0 w-max"
            >
                <FormattedMessage {...messages.nextButton} />
            </Button>
        </form>
    );
};
